import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { auth } from '../../firebase-config';

const AdminNav = ({ offersCount, packagesCount, messagesCount, isOpen, setIsOpen, handleLogout }) => {
  const closeMenu = () => {
    if (window.innerWidth <= 1024) {
      setIsOpen(false);
    }
  };

  const navLinkClass = ({ isActive }) => 
    `flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 group ${
      isActive 
        ? 'bg-black text-white shadow-md' 
        : 'text-black hover:bg-gray-100'
    }`;

  const iconClass = "text-xl group-hover:scale-110 transition-transform duration-200";
  const [pagesOpen, setPagesOpen] = useState(false);

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] lg:hidden animate-fade-in"
          onClick={() => setIsOpen(false)} 
        />
      )}

      <aside className={`fixed lg:static inset-y-0 left-0 w-72 bg-white border-r border-gray-100 z-[70] transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'} flex flex-col`}>
        <div className="p-8 border-b border-gray-50 flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-xl font-playfair font-black uppercase tracking-[0.2em] text-black">Nejira Zukić</span>
            <span className="text-[10px] text-black uppercase tracking-widest mt-1 font-bold">Admin Panel</span>
          </div>
          <button 
            className="lg:hidden p-2 text-black hover:bg-gray-100 rounded-lg"
            onClick={() => setIsOpen(false)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-4 space-y-2 mt-4">
          <NavLink to="/admin" end className={navLinkClass} onClick={closeMenu}>
            <span className={iconClass}>🏠</span> 
            <span className="font-medium tracking-wide">Početna</span>
          </NavLink>
          
          <NavLink to="/admin/messages" className={navLinkClass} onClick={closeMenu}>
            <span className={iconClass}>💬</span>
            <span className="font-medium tracking-wide flex-1">Poruke</span>
            {messagesCount > 0 && (
              <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
                {messagesCount}
              </span>
            )}
          </NavLink>

          <NavLink to="/admin/offers" className={navLinkClass} onClick={closeMenu}>
            <span className={iconClass}>📋</span>
            <span className="font-medium tracking-wide flex-1">Ponude</span>
            {offersCount > 0 && (
              <span className="bg-black text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                {offersCount}
              </span>
            )}
          </NavLink>

          <NavLink to="/admin/calendar" className={navLinkClass} onClick={closeMenu}>
            <span className={iconClass}>📅</span>
            <span className="font-medium tracking-wide">Kalendar</span>
          </NavLink>

          <NavLink to="/admin/packages" className={navLinkClass} onClick={closeMenu}>
            <span className={iconClass}>🎁</span>
            <span className="font-medium tracking-wide">Paketi</span>
          </NavLink>

          <NavLink to="/admin/extra-offers" className={navLinkClass} onClick={closeMenu}>
            <span className={iconClass}>✨</span>
            <span className="font-medium tracking-wide">Dodatne usluge</span>
          </NavLink>

          <NavLink to="/admin/sections" className={navLinkClass} onClick={closeMenu}>
            <span className={iconClass}>🖼️</span>
            <span className="font-medium tracking-wide">Sekcije</span>
          </NavLink>

          {/* Pages menu with child links (Films) */}
          <div>
            <button
              type="button"
              onClick={() => setPagesOpen(prev => !prev)}
              className="w-full text-left flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 text-black hover:bg-gray-100"
            >
              <span className={iconClass}>📄</span>
              <span className="font-medium tracking-wide flex-1">Pages</span>
              <span className={`text-[10px] transition-transform duration-200 ${pagesOpen ? 'rotate-90' : ''}`}>▶</span>
            </button>

            {pagesOpen && (
              <div className="pl-12 mt-1 space-y-1">
                <NavLink 
                  to="/admin/films" 
                  className={({isActive}) => `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${isActive ? 'bg-black text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`}
                  onClick={closeMenu}
                >
                  <span className="text-xs">🎬</span>
                  Films
                </NavLink>
              </div>
            )}
          </div>

          <NavLink to="/admin/settings" className={navLinkClass} onClick={closeMenu}>
            <span className={iconClass}>⚙️</span>
            <span className="font-medium tracking-wide">Postavke</span>
          </NavLink>
        </nav>

        <div className="p-6 bg-gray-50 border-t border-gray-100">
          <div className="flex flex-col gap-1 mb-4">
            <span className="text-xs font-bold text-black truncate uppercase tracking-tight">{auth.currentUser?.email}</span>
            <span className="text-[10px] text-black uppercase tracking-widest font-black">Administrator</span>
          </div>
          <button 
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 bg-white border border-gray-200 text-black hover:text-red-600 hover:border-red-100 hover:bg-red-50 py-2.5 rounded-lg text-xs font-bold uppercase tracking-widest transition-all mb-4"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Odjavi se
          </button>

          <div className="flex items-center justify-between pt-4 border-t border-gray-200/50">
            <span className="text-[9px] text-black uppercase tracking-widest font-black">V1.0.0</span>
            <a 
              href="/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-[9px] text-black hover:bg-gray-50 uppercase tracking-widest font-black transition-colors flex items-center gap-1"
            >
              <span>Landing Page</span>
              <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </div>
      </aside>
    </>
  );
};

export default AdminNav;
